/**
 * Zod schemas for the Search API (C13).
 *
 * Two endpoints are exposed by apps/api: keyword (Postgres FTS) and
 * semantic (pgvector kNN, optional Gemini re-rank). Both share filters,
 * cursor pagination, and a citation-bearing response shape.
 *
 * These are the canonical request/response shapes. Both the route plugins
 * (fastify-type-provider-zod) and the OpenAPI generator consume them.
 */
import { z } from "zod";
import { DocumentKindEnum } from "./document.js";

// ── Common ───────────────────────────────────────────────────────────────────

/**
 * Search-time filters. All fields are optional and AND-combined.
 *
 * - jurisdictionIds: restrict to documents whose jurisdiction is in the set.
 * - bodyNames: restrict by meeting body (e.g. "City Council").
 * - documentKinds: restrict by document_kind (agenda, minutes, ...).
 * - meetingDateFrom/To: inclusive bounds on meeting.starts_at, ISO date-time.
 */
export const SearchFiltersSchema = z
  .object({
    jurisdictionIds: z.array(z.string().uuid()).max(100).optional(),
    bodyNames: z.array(z.string().min(1).max(200)).max(100).optional(),
    documentKinds: z.array(DocumentKindEnum).max(8).optional(),
    meetingDateFrom: z.string().datetime().optional(),
    meetingDateTo: z.string().datetime().optional(),
  })
  .strict();

export type SearchFilters = z.infer<typeof SearchFiltersSchema>;

/**
 * Cursor for stable pagination. Opaque to clients; internally encodes
 * `(score, chunkId)` so that ties on score are broken deterministically.
 * Clients should pass the `nextCursor` returned by the prior page verbatim.
 */
export const SearchCursorSchema = z.string().min(1).max(512);

/**
 * One hit, with everything a citation needs.
 *
 * `snippetWithHighlights` is HTML-escaped and contains only `<mark>...</mark>`
 * tags around matched terms. It is safe to render with React's
 * `dangerouslySetInnerHTML` (see services/search/highlight.ts).
 */
export const SearchHitSchema = z.object({
  documentId: z.string().uuid(),
  // Not .uuid(): chunk ids are a deterministic content hash rendered in UUID
  // shape (stable across re-indexing), so the version/variant nibbles aren't
  // RFC-4122-compliant. They're opaque identifiers, not real UUIDs.
  chunkId: z.string().min(1),
  jurisdictionId: z.string().uuid().nullable(),
  jurisdictionName: z.string().nullable(),
  bodyName: z.string().nullable(),
  meetingDate: z.string().datetime().nullable(),
  documentKind: DocumentKindEnum.nullable(),
  documentTitle: z.string().nullable(),
  pageRange: z.tuple([z.number().int(), z.number().int()]).nullable(),
  score: z.number(),
  snippetWithHighlights: z.string(),
});

export type SearchHit = z.infer<typeof SearchHitSchema>;

// ── Keyword ──────────────────────────────────────────────────────────────────

/**
 * POST /search/keyword request body.
 *
 * `q` supports phrase queries with quotes (`"a b"`), boolean ops
 * (`AND`/`OR`/`NOT`), and negation with `-`. We parse it via Postgres
 * `websearch_to_tsquery`, which accepts that syntax verbatim and is
 * injection-safe (it never builds a tsquery by string concatenation).
 */
export const KeywordSearchRequestSchema = z
  .object({
    q: z.string().min(1).max(1000),
    filters: SearchFiltersSchema.optional(),
    limit: z.number().int().min(1).max(100).default(20),
    cursor: SearchCursorSchema.optional(),
  })
  .strict();

export type KeywordSearchRequest = z.infer<typeof KeywordSearchRequestSchema>;

export const KeywordSearchResponseSchema = z.object({
  hits: z.array(SearchHitSchema),
  nextCursor: SearchCursorSchema.nullable(),
  totalEstimated: z.number().int().nonnegative().nullable(),
  mode: z.literal("keyword"),
  elapsedMs: z.number().nonnegative(),
});

export type KeywordSearchResponse = z.infer<typeof KeywordSearchResponseSchema>;

// ── Semantic ─────────────────────────────────────────────────────────────────

/**
 * POST /search/semantic request body.
 *
 * The query is embedded server-side via Gemini text-embedding-004
 * (mockable through the embedder service). Set `rerank=true` to re-order
 * the top-50 with Gemini before returning the top `limit`.
 */
export const SemanticSearchRequestSchema = z
  .object({
    q: z.string().min(1).max(1000),
    filters: SearchFiltersSchema.optional(),
    limit: z.number().int().min(1).max(100).default(20),
    cursor: SearchCursorSchema.optional(),
    rerank: z.boolean().default(false),
  })
  .strict();

export type SemanticSearchRequest = z.infer<typeof SemanticSearchRequestSchema>;

export const SemanticSearchResponseSchema = z.object({
  hits: z.array(SearchHitSchema),
  nextCursor: SearchCursorSchema.nullable(),
  totalEstimated: z.number().int().nonnegative().nullable(),
  mode: z.literal("semantic"),
  reranked: z.boolean(),
  elapsedMs: z.number().nonnegative(),
  embeddingMs: z.number().nonnegative(),
  dbMs: z.number().nonnegative(),
});

export type SemanticSearchResponse = z.infer<typeof SemanticSearchResponseSchema>;
